import { Color } from 'highcharts';
import { ColorsList } from '../../features/core/utils';
import { overchargedTrackingColor } from '../../features/norms/utils';

export type SvgColorFunction = (color: string) => string;
export type ActualStateIconProps = (color: string, vehicleIsOverloaded:boolean, rotation:number, isSelected:boolean) => string;
export type PlaybackIconProps = (color: string, rotation:number) => string;
export type TravelPathIconProps = (color: string, rotation:number) => string;

let defaultTravelIconsSize = 24;

const Flags = {
    Red: ColorsList.red_vivid,
    Green: ColorsList.green_olive
}

export type SvgTrackingIconsType = {
    PlaybackIcon: PlaybackIconProps;
    ActualStateIcon: ActualStateIconProps,
    TravelPathIcon: TravelPathIconProps
    RedFlag: string;
    GreenFlag: string;
}

export interface IconParams {
    color: string,
    width?: number,
    height?:number,
    strokeWidth?: number,
    borderColor?:string,
    rotation?: number,
    customViewbox?:string
}

export const navigationIcon = ({
    color,
    width = defaultTravelIconsSize,
    height = defaultTravelIconsSize,
    strokeWidth = 1,
    borderColor = 'currentColor',
    rotation = 0,
    customViewbox = ''
} : IconParams) => {
    // Determine the necessary viewBox adjustments based on rotation
    let adjustedViewBox = `0 0 ${width} ${height}`;
    if(customViewbox !== ''){
        adjustedViewBox = customViewbox
    }
    let svgIcon = `<svg style="transform:rotate(${rotation}deg)" xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${adjustedViewBox}" fill="${color}" stroke="${borderColor}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-navigation ignore-drawing-tools-right-click"><path d="M12 18.5l7.265 2.463c.196 .077 .42 .032 .57 -.116a.548 .548 0 0 0 .134 -.572l-7.969 -17.275l-7.97 17.275c-.07 .2 -.017 .424 .135 .572c.15 .148 .374 .193 .57 .116l7.265 -2.463" /></svg>`;
    return svgIcon
}

export const navigationIconWithCircle = ({
    color,
    width = defaultTravelIconsSize,
    height = defaultTravelIconsSize,
    strokeWidth = 1,
    borderColor = 'currentColor',
    rotation = 0,
} : IconParams) => {
    // Determine the necessary viewBox adjustments based on rotation
    let svgIcon = `
    <svg style="transform:rotate(${rotation}deg)" 
        version="1.1" 
        xmlns="http://www.w3.org/2000/svg" 
        xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" 
        viewBox="0,0,256,256">
        <g fill="none" fill-rule="nonzero"
            stroke="none" stroke-width="2" 
            stroke-linecap="round" stroke-linejoin="round" 
            stroke-miterlimit="10" stroke-dasharray="" 
            stroke-dashoffset="0" 
            font-family="none" 
            font-weight="none" 
            font-size="none" 
            text-anchor="none" 
            style="mix-blend-mode: normal"
        >
            <g transform="translate(-52.99177,124.84078) rotate(-44) scale(10.66667,10.66667)">
                    <path d="M0,0h24v24h-24z" stroke="none" fill="${color}"></path>
                    <path d="M12,12M3,12c0,4.97056 4.02944,9 9,9c4.97056,0 9,-4.02944 9,-9c0,-4.97056 -4.02944,-9 -9,-9c-4.97056,0 -9,4.02944 -9,9" stroke="${borderColor}"></path>
                    <path d="M12,17l-1,-4l-4,-1l9,-4z" stroke="${ColorsList.black}" stroke-color="${borderColor}" stroke-width="1" fill="${color}">
                </path>
            </g>
        </g>
    </svg>`;
    return svgIcon
}

export const flagIcon = (color:string) => {
    return `
        <svg width="50px" height="50px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff" stroke-width="0.672"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#ffffff" stroke-width="0.096"></g><g id="SVGRepo_iconCarrier"> <path d="M5.75 1C6.16421 1 6.5 1.33579 6.5 1.75V3.6L8.22067 3.25587C9.8712 2.92576 11.5821 3.08284 13.1449 3.70797L13.3486 3.78943C14.9097 4.41389 16.628 4.53051 18.2592 4.1227C19.0165 3.93339 19.75 4.50613 19.75 5.28669V12.6537C19.75 13.298 19.3115 13.8596 18.6864 14.0159L18.472 14.0695C16.7024 14.5119 14.8385 14.3854 13.1449 13.708C11.5821 13.0828 9.8712 12.9258 8.22067 13.2559L6.5 13.6V21.75C6.5 22.1642 6.16421 22.5 5.75 22.5C5.33579 22.5 5 22.1642 5 21.75V1.75C5 1.33579 5.33579 1 5.75 1Z" fill="${color}"></path> </g></svg>
    `
}

export const actualStateIcon = (
    color=ColorsList.gray, 
    vehicleIsOverloaded:boolean, 
    rotation:number,
    isSelected=false
) => {
    /* Marcador para el Estado Actual de un vehiculo */
    let borderColor: string = ColorsList.black;
    let width = defaultTravelIconsSize;
    let height = defaultTravelIconsSize;
    let strokeWidth = 1;
    if(isSelected){
        borderColor = ColorsList.red;
        width = 28;
        height = 28;
    }

    if(vehicleIsOverloaded){
        strokeWidth = 2.5;
        borderColor = overchargedTrackingColor;
        width = 30;
        height = 30;
        return navigationIconWithCircle({strokeWidth, color, width, height, borderColor, rotation})
    }

    return navigationIcon({strokeWidth, color, width, height, borderColor, rotation})
}


export const playbackIcon = (color:string, rotation:number) => {
    /* Marcador para el Playback */
    return travelPathIcon(color, rotation)
}

export const travelPathIcon = (color=ColorsList.gray, rotation) => {
    /* Marcador para recorridos */
    let width = 11;
    let height = 15;
    let borderColor = 'rgba(0,0,0,0.8)';
    let customViewbox = `0 0 20 20`;
    let strokeWidth = 2;

    return navigationIcon({strokeWidth, color, width, height, borderColor, rotation, customViewbox})
}


export const SvgTrackingIcons: SvgTrackingIconsType = {
    PlaybackIcon: (color:string, rotation:number) => playbackIcon(color, rotation), //icon for playback
    ActualStateIcon: (color:string, vehicleIsOverloaded:boolean, rotation:number, isSelected:boolean) => actualStateIcon(color, vehicleIsOverloaded, rotation, isSelected), //icon for actual state
    TravelPathIcon: (color:string, rotation:number) => travelPathIcon(color, rotation), //icon for travel (yesterday, today, date_range)
    RedFlag: flagIcon(Flags.Red), //icon showed in travel path filters
    GreenFlag: flagIcon(Flags.Green) //icon showed in travel path filters
}


